import { isEqual } from 'lodash';
import { memo } from 'react';

import { type PublicClip } from '@air/api';

import { type GalleryViewRender } from '~/components/Gallery/types';
import { useGetPublicAssetMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicAssetMenuOptionsCallback';
import { GalleryFile } from '~/components/v2/components/shared/gallery/GalleryFile';
import { usePublicGalleryFileCardProps } from '~/components/v2/components/shared/gallery/usePublicGalleryFileCardProps';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';

export type PublicGalleryFileProps = GalleryViewRender<PublicClip>;

export const PublicGalleryFile = memo(
  ({ data: file }: PublicGalleryFileProps) => {
    // The card body (preview, title, click targets, indicators) is the shared public contract —
    // the editor's canvas card renders the same body through this hook; only the menus below are
    // public-specific.
    const fileCardProps = usePublicGalleryFileCardProps({ file });
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getAssetMenuOptionsCallback } = useGetPublicAssetMenuOptionsCallback();
    const getItemMenuOptions = getAssetMenuOptionsCallback({ item: file });

    return (
      <GalleryFile
        file={file}
        isSelectable={true}
        key={file.id}
        getItemMenuOptions={getItemMenuOptions}
        getSelectionMenuOptions={getSelectionMenuOptions}
        {...fileCardProps}
      />
    );
  },
  // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
  isEqual,
);

PublicGalleryFile.displayName = 'PublicGalleryFile';
